import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { RJ_VISIBILITY_OPT_IN_COPY, RJ_VISIBILITY_OPT_OFF_COPY } from './copy';

// RJ Seller Visibility Workflow — inline toggle sheet for the LaunchPad
// quickstart card.
//
// "More easily" in the user story means fewer clicks: the seller confirms and
// toggles here rather than being routed to /succession/settings. The mutation,
// the confirmation copy and the resulting DB state are identical to the
// Settings surface — only the entry point differs. Copy comes from ./copy so
// the two surfaces cannot drift.
//
// Profile-completion honesty: opting in does NOT make a seller searchable if
// they fail `minimum_profile_completed?` (see `User#is_searchable?`). Rather
// than let them flip the switch and silently appear in zero searches, this
// sheet refuses the toggle and routes to the profile instead. Note this gate
// reads `minimum_profile_completed`, NOT `profile_section_percentage` — those
// are different measures and a seller can sit at 85% while still failing the
// searchability gate.
//
// See docs/plans/2026-07-17-001-feat-rj-seller-visibility-workflow-plan.md

const RJ_TOGGLE_VISIBILITY = gql`
  mutation RjToggleVisibility($enabled: Boolean!) {
    rj_toggle_visibility(enabled: $enabled) {
      success
      errors
      marketplace_visibility_opt_in
    }
  }
`;

const track = (event, payload) => {
  if (typeof window !== 'undefined' && window.ahoy) {
    try {
      window.ahoy.track(event, payload);
    } catch (e) {
      // Tracking must never break the toggle.
    }
  }
};

const RjVisibilitySheet = ({ open, optedIn, minimumProfileCompleted, onClose, onToggled }) => {
  const [error, setError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [rjToggleVisibility, { loading }] = useMutation(RJ_TOGGLE_VISIBILITY);

  if (!open) return null;

  // The action the seller is about to take is the opposite of current state.
  const intent = optedIn ? 'disable' : 'enable';
  const nextValue = intent === 'enable';
  const blockedOnProfile = nextValue && !minimumProfileCompleted;

  const handleConfirm = async () => {
    setError(null);
    try {
      const result = await rjToggleVisibility({ variables: { enabled: nextValue } });
      const payload = result?.data?.rj_toggle_visibility;
      if (payload?.success) {
        // Surface-specific event for measuring the card itself, plus the
        // canonical opt-in/opt-out event so tenant-level opt-in rate covers
        // both this card and the Settings toggle.
        track('rj_visibility_card_toggled', { enabled: nextValue });
        track(nextValue ? 'rj_visibility_opt_in' : 'rj_visibility_opt_out', { source: 'launchpad_card' });
        onToggled(payload.marketplace_visibility_opt_in);
        onClose();
      } else {
        setError((payload?.errors || []).join(', ') || 'Update failed. Please try again.');
      }
    } catch (e) {
      console.error('[RJ Visibility] card toggle failed:', e);
      setError('Update failed. Please try again.');
    }
  };

  let title;
  if (blockedOnProfile) {
    title = (
      <FormattedMessage
        id="succession.launchpad.rj_visibility.sheet_title_profile_incomplete"
        defaultMessage="Complete your profile first"
      />
    );
  } else if (intent === 'enable') {
    title = (
      <FormattedMessage
        id="settings.succession.rj_visibility.modal_title_enable"
        defaultMessage="Turn marketplace visibility on?"
      />
    );
  } else {
    title = (
      <FormattedMessage
        id="settings.succession.rj_visibility.modal_title_disable"
        defaultMessage="Turn marketplace visibility off?"
      />
    );
  }

  // Paige (2026-09-01): "a checkbox alone feels underwhelming" for opting IN.
  // Strengthened by making the disclaimer an ACTIVE acknowledgement that gates
  // the confirm button, rather than passive text above it.
  //
  // ⚠️ The disclaimer wording itself is untouched on purpose — copy.js is verbatim
  // from Ashley (Raymond James) and legal sign-off was waived on condition that
  // the reversibility sentence is surfaced. This changes the interaction, not a
  // single word of the approved text.
  //
  // Opt-OFF is not gated: turning visibility back off is the safe direction, and
  // adding friction there would work against the reversibility promise.
  const requiresAck = intent === 'enable' && !blockedOnProfile;
  const confirmDisabled = loading || (requiresAck && !acknowledged);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-succession-bg-primary border border-succession-border rounded-lg max-w-md w-full shadow-xl transition-colors">
        <div className="px-6 py-4 border-b border-succession-border">
          <h3 className="text-lg font-semibold text-succession-text-primary">{title}</h3>
        </div>

        <div className="p-6 space-y-4">
          {blockedOnProfile ? (
            <p className="text-sm text-succession-text-primary leading-relaxed">
              <FormattedMessage
                id="succession.launchpad.rj_visibility.sheet_profile_incomplete_body"
                defaultMessage="Buyers can only find advisors with a completed profile. Finish your profile and you'll be able to turn visibility on."
              />
            </p>
          ) : (
            <p className="text-sm text-succession-text-primary leading-relaxed">
              {intent === 'enable' ? RJ_VISIBILITY_OPT_IN_COPY : RJ_VISIBILITY_OPT_OFF_COPY}
            </p>
          )}
          {requiresAck && (
            <label className="flex items-start gap-2 text-sm text-succession-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <FormattedMessage
                  id="settings.succession.rj_visibility.modal_acknowledge"
                  defaultMessage="I understand that buyers will be able to find and contact me, and that I can turn this off at any time."
                />
              </span>
            </label>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-succession-border flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-succession-text-secondary hover:text-succession-text-primary border border-succession-border rounded-lg hover:bg-succession-bg-secondary transition-colors"
          >
            <FormattedMessage id="settings.succession.buttons.cancel" defaultMessage="Cancel" />
          </button>
          {blockedOnProfile ? (
            <a
              href="/succession/profile"
              onClick={() => track('rj_visibility_card_profile_redirect')}
              className="px-4 py-2 text-sm font-medium bg-succession-primary hover:bg-succession-primary-dark text-white rounded-lg transition-colors no-underline"
            >
              <FormattedMessage
                id="succession.launchpad.rj_visibility.sheet_go_to_profile"
                defaultMessage="Go to my profile"
              />
            </a>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className="px-4 py-2 text-sm font-medium bg-succession-primary hover:bg-succession-primary-dark text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {intent === 'enable' ? (
                <FormattedMessage
                  id="settings.succession.rj_visibility.modal_confirm_enable"
                  defaultMessage="Turn on visibility"
                />
              ) : (
                <FormattedMessage
                  id="settings.succession.rj_visibility.modal_confirm_disable"
                  defaultMessage="Turn off visibility"
                />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

RjVisibilitySheet.propTypes = {
  open: PropTypes.bool,
  optedIn: PropTypes.bool,
  minimumProfileCompleted: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onToggled: PropTypes.func.isRequired,
};

RjVisibilitySheet.defaultProps = {
  open: false,
  optedIn: false,
  minimumProfileCompleted: false,
};

export default RjVisibilitySheet;
